import React, { useState } from 'react';
import { Modal, Form, Input, Button, message, Typography } from 'antd';
import {
  performTenantLogin,
  isTenantTeamPick,
  type TeamPickCandidate,
  type TenantLoginSuccess
} from '../services/tenantLogin';
import TeamPickModal from './TeamPickModal';

const { Text } = Typography;

export type WechatWebBindModalProps = {
  open: boolean;
  bindToken: string;
  onClose: () => void;
  onBindSuccess: (
    result: TenantLoginSuccess,
    values: { teamCode?: string; username: string }
  ) => void;
};

const WechatWebBindModal: React.FC<WechatWebBindModalProps> = ({
  open,
  bindToken,
  onClose,
  onBindSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [teamPickOpen, setTeamPickOpen] = useState(false);
  const [candidates, setCandidates] = useState<TeamPickCandidate[]>([]);
  const [pickLoading, setPickLoading] = useState(false);

  const runLogin = async (selectedUserId?: string) => {
    const v = await form.validateFields();
    const teamCode = String(v.teamCode || '').trim();
    const username = String(v.username || '').trim();
    const password = String(v.password || '');
    const result = await performTenantLogin(teamCode, username, password, selectedUserId, bindToken);

    if (isTenantTeamPick(result)) {
      setCandidates(result.candidates);
      setTeamPickOpen(true);
      message.warning(result.error);
      return;
    }
    if (!result.ok) {
      message.error(result.error);
      return;
    }
    onBindSuccess(result, { teamCode: teamCode || undefined, username });
    form.resetFields();
    onClose();
  };

  const handleFinish = async () => {
    if (!bindToken || loading) return;
    setLoading(true);
    try {
      await runLogin();
    } catch {
      message.error('请检查表单填写');
    } finally {
      setLoading(false);
    }
  };

  const handleTeamPickConfirm = async (userId: string) => {
    if (!userId || pickLoading) return;
    setPickLoading(true);
    try {
      await runLogin(userId);
      setTeamPickOpen(false);
    } catch {
      message.error('选择团队失败');
    } finally {
      setPickLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        title="绑定微信账号"
        footer={null}
        destroyOnHidden
        width={440}
        className="wechat-web-bind-modal"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          当前微信尚未绑定系统账号，请输入实验室账号与密码完成绑定。绑定成功后，下次可直接使用微信扫码登录。
        </Text>
        <Form form={form} layout="vertical" size="large" onFinish={handleFinish} colon={false}>
          <Form.Item name="teamCode" label="团队编码（选填）">
            <Input placeholder="例如 LAB-204-PX" autoComplete="organization" />
          </Form.Item>
          <Form.Item
            name="username"
            label="用户名 / 学号 / 邮箱"
            rules={[{ required: true, message: '请输入账号' }]}
          >
            <Input placeholder="用户名、学号或邮箱" autoComplete="username" />
          </Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }]}>
            <Input.Password placeholder="请输入密码" autoComplete="current-password" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              绑定并登录
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <TeamPickModal
        open={teamPickOpen}
        candidates={candidates}
        confirmLoading={pickLoading}
        title="选择要进入的团队"
        onCancel={() => setTeamPickOpen(false)}
        onConfirm={handleTeamPickConfirm}
      />
    </>
  );
};

export default WechatWebBindModal;
